import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Loader2 } from "lucide-react";
import SlideForm from "./SlideForm";
import { resolveImg } from "@/lib/marketplaceTrack";

const strip = ({ id, created_at, updated_at, ...rest }) => rest;

export default function SlidesManager() {
  const [slides, setSlides] = useState(null);
  const [listings, setListings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [catNames, setCatNames] = useState({});

  const load = async () => {
    const [s, l, c] = await Promise.all([api.get("/admin/marketplace/slides"), api.get("/admin/marketplace/listings"), api.get("/admin/marketplace/categories")]);
    setSlides(s.data);
    setListings(l.data.filter((x) => x.status !== "archived"));
    setCatNames(Object.fromEntries(c.data.map((x) => [x.key, x.name])));
  };
  useEffect(() => { load().catch(() => toast.error("Could not load slides")); }, []);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/admin/marketplace/slides/${form.id}`, strip(form));
      else await api.post("/admin/marketplace/slides", strip(form));
      toast.success("Slide saved"); setEditing(null); load();
    } catch { toast.error("Save failed — title and image are required"); }
  };
  const toggle = async (s) => {
    try { await api.put(`/admin/marketplace/slides/${s.id}`, { ...strip(s), is_active: !s.is_active }); load(); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove slide "${s.title}"?`)) return;
    try { await api.delete(`/admin/marketplace/slides/${s.id}`); toast.success("Slide removed"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= slides.length) return;
    const next = [...slides];
    [next[i], next[j]] = [next[j], next[i]];
    setSlides(next);
    try { await api.post("/admin/marketplace/slides/reorder", { ids: next.map((x) => x.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  if (!slides) return <Loader2 className="animate-spin text-[#D4AF37]" />;
  return (
    <div data-testid="mkt-slides-manager">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <p className="text-sm text-neutral-400">Slides rotate every 6 seconds at the top of the marketplace. Use 16:9 images (1920×1080).</p>
        <button data-testid="mkt-add-slide" onClick={() => setEditing({})} className="btn-gold px-4 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2">
          <Plus size={14} /> New slide
        </button>
      </div>
      <div className="space-y-3">
        {slides.map((s, i) => (
          <div key={s.id} data-testid={`mkt-slide-row-${s.id}`} className={`luxury-card rounded-lg p-3 flex items-center gap-4 ${s.is_active ? "" : "opacity-50"}`}>
            <div className="flex flex-col">
              <button aria-label="Move up" data-testid={`mkt-slide-up-${s.id}`} onClick={() => move(i, -1)} className="p-1 text-neutral-500 hover:text-white"><ArrowUp size={13} /></button>
              <button aria-label="Move down" data-testid={`mkt-slide-down-${s.id}`} onClick={() => move(i, 1)} className="p-1 text-neutral-500 hover:text-white"><ArrowDown size={13} /></button>
            </div>
            <img src={resolveImg(s.image_url)} alt="" className="w-32 aspect-video object-cover rounded" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">{String(i + 1).padStart(2, "0")} · {s.eyebrow}</div>
              <div className="text-white truncate">{s.title}</div>
              <div className="text-xs text-neutral-500 truncate">{s.cta_label} → {s.cta_url}</div>
            </div>
            <button title={s.is_active ? "Hide" : "Show"} data-testid={`mkt-slide-toggle-${s.id}`} onClick={() => toggle(s)} className="p-2 text-neutral-400 hover:text-white">{s.is_active ? <Eye size={15} /> : <EyeOff size={15} />}</button>
            <button title="Edit" data-testid={`mkt-slide-edit-${s.id}`} onClick={() => setEditing(s)} className="p-2 text-neutral-400 hover:text-[#D4AF37]"><Pencil size={15} /></button>
            <button title="Delete" data-testid={`mkt-slide-delete-${s.id}`} onClick={() => remove(s)} className="p-2 text-neutral-400 hover:text-red-400"><Trash2 size={15} /></button>
          </div>
        ))}
        {!slides.length && <div className="text-neutral-500 text-sm py-6">No slides — the static hero will show instead.</div>}
      </div>
      {editing && <SlideForm initial={editing} listings={listings} catNames={catNames} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}
